import { useState } from 'react';
// @mui
import {
  AppBar,
  Box,
  Container,
  Drawer,
  IconButton,
  Link,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
// utils
import { bgBlur } from '../../utils/cssStyles';
import smoothScrollTo from '../../utils/smoothScroll';
// config
import { HEADER } from '../../config-global';
// content
import useContent from '../../content';
// locales
import useLocales from '../../locales/useLocales';
// components
import Iconify from '../../components/iconify';
import Logo from '../../components/logo';
import useOffSetTop from '../../hooks/useOffSetTop';
import useResponsive from '../../hooks/useResponsive';

// ----------------------------------------------------------------------

const linkStyle = {
  color: 'common.white',
  letterSpacing: '0.12em',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  borderBottom: '1px solid transparent',
  transition: 'border-color 0.2s, color 0.2s',
  '&:hover': { borderBottomColor: '#3494BA', color: '#3494BA' },
} as const;

// ----------------------------------------------------------------------

function LanguageToggle() {
  const { currentLang, onChangeLang, allLangs } = useLocales();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {allLangs.map((lang, index) => {
        const active = lang.value === currentLang.value;
        return (
          <Box key={lang.value} sx={{ display: 'flex', alignItems: 'center' }}>
            {index > 0 && (
              <Typography sx={{ mx: 0.75, color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>
                |
              </Typography>
            )}
            <Typography
              onClick={() => onChangeLang(lang.value)}
              sx={{
                cursor: 'pointer',
                fontSize: '0.8rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: active ? 'common.white' : 'rgba(255,255,255,0.4)',
                transition: 'color 0.2s',
                '&:hover': { color: active ? 'common.white' : '#3494BA' },
              }}
            >
              {lang.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ----------------------------------------------------------------------

export default function Header() {
  const theme = useTheme();
  const isDesktop = useResponsive('up', 'md');
  const isOffset = useOffSetTop(HEADER.H_MAIN_DESKTOP);

  const { UI } = useContent();

  const [openDrawer, setOpenDrawer] = useState(false);

  const navItems = [
    { label: UI.navAnsatz, id: '#ansatz' },
    { label: UI.navLeistungen, id: '#overview' },
    { label: UI.navTeam, id: '#team' },
  ];

  const scrollTo = (id: string) => {
    const target = document.querySelector(id);
    if (target) smoothScrollTo(target);
  };

  const handleDrawerNavClick = (id: string) => {
    setOpenDrawer(false);
    // wait for the drawer to release its scroll lock before scrolling
    setTimeout(() => scrollTo(id), 300);
  };

  return (
    <AppBar color="transparent" sx={{ boxShadow: 0 }}>
      <Toolbar
        disableGutters
        sx={{
          py: 1,
          transition: theme.transitions.create(['padding', 'background-color'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shorter,
          }),
          ...(isOffset && {
            ...bgBlur({ color: theme.palette.background.default }),
            py: 1,
          }),
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            height: 1,
            display: 'flex',
            alignItems: 'center',
            // logo far-left, flush with the side-nav entries (same pl as
            // HomeSideNav); right padding mirrors it so the language toggle
            // keeps the same margin to the right edge
            pl: { xs: 3, lg: 5, xl: 7 },
            pr: { xs: 3, lg: 5, xl: 7 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, md: 2, lg: 0 } }}>
            <Logo />
          </Box>

          {isDesktop ? (
            <>
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {navItems.map((item) => (
                  <Link
                    key={item.id}
                    href={item.id}
                    underline="none"
                    sx={{ ml: 4, ...linkStyle }}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollTo(item.id);
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Link
                  href="mailto:kontakt@fpv-bt.com"
                  underline="none"
                  sx={{ ml: 2, px: 1, py: 0.5, ...linkStyle }}
                >
                  {UI.contact}
                </Link>

                <Box sx={{ ml: 3 }}>
                  <LanguageToggle />
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                onClick={() => setOpenDrawer(true)}
                sx={{ color: 'common.white' }}
                aria-label="menu"
              >
                <Iconify icon="eva:menu-2-fill" width={26} />
              </IconButton>
            </Box>
          )}
        </Container>
      </Toolbar>

      {/* Mobile navigation drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: 300,
            maxWidth: '85vw',
            bgcolor: '#1B1E22',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
          <IconButton
            onClick={() => setOpenDrawer(false)}
            sx={{ color: 'rgba(255,255,255,0.6)' }}
            aria-label="close menu"
          >
            <Iconify icon="eva:close-fill" width={26} />
          </IconButton>
        </Box>

        <Stack spacing={3.5}>
          {navItems.map((item) => (
            <Typography
              key={item.id}
              onClick={() => handleDrawerNavClick(item.id)}
              sx={{
                cursor: 'pointer',
                color: 'common.white',
                fontWeight: 700,
                fontSize: '1.4rem',
                lineHeight: 1.3,
                '&:hover': { color: '#3494BA' },
              }}
            >
              {item.label}
            </Typography>
          ))}

          <Link
            href="mailto:kontakt@fpv-bt.com"
            underline="none"
            sx={{
              color: 'common.white',
              fontWeight: 700,
              fontSize: '1.4rem',
              lineHeight: 1.3,
              '&:hover': { color: '#3494BA' },
            }}
          >
            {UI.contact}
          </Link>
        </Stack>

        <Box sx={{ mt: 'auto', pt: 4 }}>
          <LanguageToggle />
        </Box>
      </Drawer>
    </AppBar>
  );
}
